import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPlaceRoleDefaults,
  setPlaceRoleDefaults,
  listPlaceRoleDayDefaults,
  setPlaceRoleDayDefaults,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { RoleRequirementsEditor, type RoleRequirementRow } from './RoleRequirementsEditor';

interface PlaceStaffingManagerProps {
  placeId: string;
}

interface DayOverride {
  day: string; // YYYY-MM-DD
  rows: RoleRequirementRow[];
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Edits a stage's default role requirements: a base set plus optional per-day
 * overrides. These prefill the requirements of happenings created on this stage;
 * a happening on a day with an override uses that day's counts instead of the base.
 */
export function PlaceStaffingManager({ placeId }: PlaceStaffingManagerProps) {
  const queryClient = useQueryClient();

  const { data: loadedBase, isLoading: loadingBase } = useQuery({
    queryKey: ['place-role-defaults', placeId],
    queryFn: () => listPlaceRoleDefaults(placeId),
  });
  const { data: loadedDays, isLoading: loadingDays } = useQuery({
    queryKey: ['place-role-day-defaults', placeId],
    queryFn: () => listPlaceRoleDayDefaults(placeId),
  });

  const [rows, setRows] = useState<RoleRequirementRow[]>([]);
  const [overrides, setOverrides] = useState<DayOverride[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRows((loadedBase ?? []).map((d) => ({ role_id: d.role_id, count: d.count })));
    setDirty(false);
  }, [loadedBase]);

  useEffect(() => {
    // Group flat day rows into per-day editors.
    const byDay = new Map<string, RoleRequirementRow[]>();
    for (const d of loadedDays ?? []) {
      (byDay.get(d.day) ?? byDay.set(d.day, []).get(d.day)!).push({ role_id: d.role_id, count: d.count });
    }
    setOverrides(Array.from(byDay.entries()).map(([day, r]) => ({ day, rows: r })));
    setDirty(false);
  }, [loadedDays]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await setPlaceRoleDefaults(placeId, rows);
      const flat = overrides
        .filter((o) => o.day)
        .flatMap((o) => o.rows.map((r) => ({ role_id: r.role_id, day: o.day, count: r.count })));
      await setPlaceRoleDayDefaults(placeId, flat);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-role-defaults', placeId] });
      queryClient.invalidateQueries({ queryKey: ['place-role-day-defaults', placeId] });
      setDirty(false);
    },
  });

  if (loadingBase || loadingDays) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const updateOverride = (index: number, patch: Partial<DayOverride>) => {
    setOverrides((prev) => prev.map((o, i) => (i === index ? { ...o, ...patch } : o)));
    setDirty(true);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Base staffing for performances on this stage. When you create a happening here, these
          prefill its role requirements.
        </p>
        <RoleRequirementsEditor
          value={rows}
          onChange={(next) => {
            setRows(next);
            setDirty(true);
          }}
        />
      </div>

      <div className="space-y-3 border-t border-input pt-4">
        <div>
          <h4 className="text-sm font-medium">Day-specific overrides</h4>
          <p className="text-xs text-muted-foreground">
            For days where this stage needs different amounts, set the counts here. A happening on an
            override day uses that day's counts instead of the base.
          </p>
        </div>

        {overrides.length === 0 && (
          <p className="text-sm text-muted-foreground">No day overrides.</p>
        )}

        {overrides.map((ov, index) => (
          <div key={index} className="space-y-2 rounded-md border border-input p-3">
            <div className="flex items-center justify-between gap-2">
              <input
                type="date"
                value={ov.day}
                onChange={(e) => updateOverride(index, { day: e.target.value })}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setOverrides((prev) => prev.filter((_, i) => i !== index));
                  setDirty(true);
                }}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <RoleRequirementsEditor
              value={ov.rows}
              onChange={(next) => updateOverride(index, { rows: next })}
            />
          </div>
        ))}

        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setOverrides((prev) => [...prev, { day: todayStr(), rows: rows.map((r) => ({ ...r })) }]);
            setDirty(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add day override
        </Button>
      </div>

      <div className="flex justify-end">
        <Button type="button" onClick={() => saveMutation.mutate()} disabled={!dirty || saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save defaults'}
        </Button>
      </div>
    </div>
  );
}
